import * as XLSX from "xlsx-js-style";

function extractExcelJson(content: string): boolean {
    try {
      // Check for content wrapped in excel tags using [\s\S]* instead of /s flag
      const excelRegex = /<excel>([\s\S]*?)<\/excel>/;
      const match = content.match(excelRegex);
  
      if (!match) return false;
  
      // Parse and validate JSON content
      const jsonContent = JSON.parse(match[1]);
      return jsonContent;
    } catch {
      return false;
    }
}

export const getWorkbookfromJson = async (requestBody) => {
  const requestJson = extractExcelJson(requestBody);

  if (typeof requestJson === "object" && requestJson !== null) {
    let wb: any = XLSX.utils.book_new();
  
    // Create the Lookup table sheet
    if ("Lookup Tables" in requestJson) {
      wb = orchistrate_worksheet_workflow(
        requestJson["Lookup Tables"],
        "Lookup Tables",
        wb,
        table_ranges_by_sheet["Lookup Tables"]
      );
    }
  
    // Process each additional page
    Object.keys(requestJson).forEach((sheet_name) => {
      if (!["Lookup Tables"].includes(sheet_name)) {
        orchistrate_worksheet_workflow(
          requestJson[sheet_name],
          sheet_name,
          wb,
          false
        );
      }
    });
    
    // Ensure column visibility for all worksheets in the workbook
    wb.SheetNames.forEach(sheetName => {
      const sheet = wb.Sheets[sheetName];
      ensureColumnVisibility(sheet);
    });
  
    // Generate Excel file
    XLSX.writeFile(wb, "data.xlsx");
    return true;
  } else {
    return false
  }
};

const default_border_color = "5b5b5b";

const excelThemeColors = {
  1: "1F497D", // Dark Blue
  2: "C6E0B4", // Light Green
  3: "E0EAF6", // Light Blue
  4: "F4CCCC", // Light Red
  5: "FCE4D6", // Light Orange
  6: "FFE599", // Light Yellow
  7: "9FCE63", // Light Green
  8: "A2C4C9", // Light Teal
  9: "EAD1DC", // Light Purple
  10: "E5F0DD", // Light Lime
  11: "F4CCCC", // Light Red
};

const table_ranges_by_sheet = {
  "Lookup Tables": [
    {
      name: "MaturityTable",
      formulae_range: "'Lookup Tables'!$F$7:$G$12",
      range: "F7:G12",
    },
    {
      name: "ImplementationTable",
      formulae_range: "'Lookup Tables'!$H$7:$I$12",
      range: "H7:I12",
    },
    {
      name: "AnswerTable",
      formulae_range: "'Lookup Tables'!$K$7:$M$17",
      range: "K7:M17",
    },
    {
      name: "WeightingTable",
      formulae_range: "'Lookup Tables'!$E$23:$F$27",
      range: "E23:F27",
    },
  ],
};

const formulae_replacements = {
  // Removes leading '=' and optional spaces
  "^=\\s*": "",
  // Removes '[]' - to Excel this means all cells in the table, to this library it doesn't mean anything
  "\\[\\]": "",
  // Replaces 'AnswerTable[Answer]' with the new reference - to Excel this means all cells in the column, to this library it doesn't mean anything
  "AnswerTable\\[Answer\\]": "'Lookup Tables'!K7:K17",
  // Replaces 'AnswerTable[Score]' with the new reference - to Excel this means all cells in the column, to this library it doesn't mean anything
  "AnswerTable\\[Score\\]": "'Lookup Tables'!L7:L17",
};

function extract_style_tag_value(
  styleString,
  property,
  matchIndex = 0,
  customLog = false
) {
  // Remove newlines and replace with tabs for better parsing
  styleString = styleString.replace(/[\n]/g, " ");

  // Case 2: Direct property extraction
  const directPattern = new RegExp(`${property}=([^,\\n]+)`, "i");
  const directMatch = styleString.match(directPattern);

  if (directMatch) {
    let value = directMatch[1].trim();
    // Remove quotes if present
    if (value.startsWith("'") && value.endsWith("'")) {
      value = value.substring(1, value.length - 1);
    }
    return value === "None" ? null : value;
  }

  return null;
}

const reorder_merge_list_alphanumerically = (excel_cell_positions) => {
  // Function to convert a column label (e.g., "D", "AB") to a number
  const columnToNumber = (column) => {
    let number = 0;
    for (let i = 0; i < column.length; i++) {
      number = number * 26 + (column.charCodeAt(i) - "A".charCodeAt(0) + 1);
    }
    return number;
  };

  // Function to parse the cell (e.g., "D6")
  const parseCell = (cell) => {
    const match = cell.match(/^([A-Z]+)(\d+)$/);
    const column = match[1];
    const row = parseInt(match[2]);
    return { column, row, columnNumber: columnToNumber(column) };
  };

  // Function to compare two pairs of cells (e.g., "D6:E6" vs "B6:C6")
  const compareCells = (a, b) => {
    const [startA, endA] = a.split(":");
    const [startB, endB] = b.split(":");

    // Compare starting cell first
    const cellA = parseCell(startA);
    const cellB = parseCell(startB);

    // Compare by column number, then row
    if (cellA.columnNumber !== cellB.columnNumber) {
      return cellA.columnNumber - cellB.columnNumber;
    }
    return cellA.row - cellB.row;
  };

  // Sort the list
  return excel_cell_positions.sort(compareCells);
};

/**
 * Ensures all columns are visible by resetting any hidden status
 * @param ws - Worksheet to process
 * @returns The processed worksheet with visible columns
 */
const ensureColumnVisibility = (ws: any): any => {
  if (!ws || !ws['!cols']) return ws;
  
  // Iterate through all column definitions and ensure they're not hidden
  ws['!cols'].forEach((col: any, idx: number) => {
    if (col) {
      // Remove hidden property or set to false explicitly
      delete col.hidden;
      
      // Ensure reasonable width for any merged column areas
      if (!col.wch || col.wch < 8.43) {
        col.wch = 8.43; // Default Excel column width
      }
    }
  });
  
  return ws;
};

const pad_merged_cell_rows = (sheet_data, merged_cells) => {
  const adjustedData: any = [];
  const adjustedValues: any = [];
  const mergedCellsMap = {};
  // Track which columns are part of merged areas to ensure they remain visible
  const mergedColumnIndices: Set<number> = new Set();

  /**
     * Transform the Excel cell positioning a list of numeric column row positions
     * Below is an example of the output for row 1, it has 1 merge from col B2:M3
     * [ 
        {
          "mergeStr": "B2:M3",
          "startCell": {
            "c": 2,
            "r": 2
          },
          "endCell": {
            "c": 13,
            "r": 3
          }
        }
      ]
    */
  merged_cells.forEach((mergeStr, index) => {
    const [start, end] = mergeStr.split(":");

    // StartCell seems to think that column A is treated as a 0 & row 1 is treated as a 0
    // Hence (decode + 1)
    const startCell = XLSX.utils.decode_cell(start);
    startCell["c"] = startCell["c"] + 1;
    startCell["r"] = startCell["r"] + 1;
    const endCell = XLSX.utils.decode_cell(end);
    endCell["c"] = endCell["c"] + 1;
    endCell["r"] = endCell["r"] + 1;

    // Track all columns in the merge range to ensure they remain visible
    for (let c = startCell["c"]; c <= endCell["c"]; c++) {
      mergedColumnIndices.add(c - 1); // Adjust back to 0-based for Excel.js
    }

    // Create the merge object
    const cell = {
      mergeStr: mergeStr,
      startCell: startCell,
      endCell: endCell,
    };

    // Instanciate the row
    if (!mergedCellsMap[startCell["r"]]) {
      mergedCellsMap[startCell["r"]] = [];
    }

    // Add the merge object to the new || existing row
    mergedCellsMap[startCell["r"]].push(cell);
  });

  // Process the data to account for merged cells
  for (let dataRowIndex = 0; dataRowIndex < sheet_data.length; dataRowIndex++) {
    const row = sheet_data[dataRowIndex];
    const adjustedRowValues: any = [];
    const adjustedRowData: any = [];
    const empty_cell_data = {
      value: "",
      formula: null,
      style: {
        font: "<openpyxl.styles.fonts.Font object>\nParameters:\nname='Arial', charset=None, family=2.0, b=True, i=False, strike=None, outline=None, shadow=None, condense=None, color=<openpyxl.styles.colors.Color object>\nParameters:\nrgb=None, indexed=None, auto=None, theme=1, tint=0.0, type='theme', extend=None, sz=12.0, u=None, vertAlign=None, scheme=None",
        fill: "<openpyxl.styles.fills.PatternFill object>\nParameters:\npatternType='solid', fgColor=<openpyxl.styles.colors.Color object>\nParameters:\nrgb=None, indexed=None, auto=None, theme=0, tint=-0.1499984740745262, type='theme', bgColor=<openpyxl.styles.colors.Color object>\nParameters:\nrgb=None, indexed=64, auto=None, theme=None, tint=0.0, type='indexed'",
        border:
          "<openpyxl.styles.borders.Border object>\nParameters:\noutline=True, diagonalUp=False, diagonalDown=False, start=None, end=None, left=<openpyxl.styles.borders.Side object>\nParameters:\nstyle=None, color=<openpyxl.styles.colors.Color object>\nParameters:\nrgb=None, indexed=64, auto=None, theme=None, tint=0.0, type='indexed', right=<openpyxl.styles.borders.Side object>\nParameters:\nstyle=None, color=<openpyxl.styles.colors.Color object>\nParameters:\nrgb=None, indexed=None, auto=True, theme=None, tint=0.0, type='auto', top=<openpyxl.styles.borders.Side object>\nParameters:\nstyle=None, color=None, bottom=<openpyxl.styles.borders.Side object>\nParameters:\nstyle=None, color=None, diagonal=<openpyxl.styles.borders.Side object>\nParameters:\nstyle=None, color=None, vertical=None, horizontal=None",
        alignment:
          "<openpyxl.styles.alignment.Alignment object>\nParameters:\nhorizontal='center', vertical=None, textRotation=0, wrapText=None, shrinkToFit=None, indent=0.0, relativeIndent=0.0, justifyLastLine=None, readingOrder=0.0",
        number_format: "General",
      },
      number_format: "General",
      row_dimensions: null,
      column_dimensions: 16.7265625,
      merged: true,
    };

    // Are there merges in this row?
    // (Use the Excel Index: starts at 1, not 0)
    let merged_bounding_areas = mergedCellsMap[dataRowIndex + 1];

    // X offset made by the data not accounting for the padding needed for cell merges in Excel
    let merge_excel_padding_count = 0;

    // Itterate each column in this row
    for (let cellIndex = 0; cellIndex < row.length; cellIndex++) {
      // Get the cell object
      const cell = row[cellIndex];

      // Excel cells start with index (column) = 1, not 0
      const colIndex = cellIndex + 1;

      // Does this row contain merged cells?
      if (merged_bounding_areas) {
        // Check for if the cell is not a merged cell
        let is_added = false;

        // Itterate each merged bounding area e.g. B1:E5
        for (
          let mba_index = 0;
          mba_index < merged_bounding_areas.length;
          mba_index++
        ) {
          // Get the bounding area from the index
          const merged_bounding_area = merged_bounding_areas[mba_index];

          // is the start column of the current match equal to the current index + 1
          if (
            merged_bounding_area["startCell"]["c"] ===
            colIndex + merge_excel_padding_count
          ) {
            adjustedRowValues.push(cell["value"]);
            adjustedRowData.push(cell);

            for (
              let m = 0;
              m <
              merged_bounding_area["endCell"]["c"] -
                merged_bounding_area["startCell"]["c"];
              m++
            ) {
              // Push dummy data to be replaced by the actual merge command
              adjustedRowValues.push("merge");
              adjustedRowData.push(empty_cell_data);

              // Increment the new X backoff
              merge_excel_padding_count += 1;
            }

            // Mark the cell as already incerted
            is_added = true;

            // Break the immediate loop, save compute
            break;
          } else {
            // Do nothing, see '!is_added' below
          }
        }

        // If the cell is not a merged cell, set the value as is
        if (!is_added) {
          adjustedRowValues.push(cell["value"]);
          adjustedRowData.push(cell);
        }
      } else {
        adjustedRowValues.push(cell["value"]);
        adjustedRowData.push(cell);
      }
    }

    // Add the row to the final object
    adjustedValues.push(adjustedRowValues);
    adjustedData.push(adjustedRowData);
  }

  return { data: adjustedData, values: adjustedValues, mergedColumnIndices: mergedColumnIndices };
};

const merge_cells_from_merge_list = (ws, merged_cells) => {
  if (!ws["!merges"]) ws["!merges"] = [];

  if (merged_cells) {
    merged_cells.forEach((mergeStr) => {
      const [start, end] = mergeStr.split(":");
      const startCell = XLSX.utils.decode_cell(start);
      const endCell = XLSX.utils.decode_cell(end);

      const merge = {
        s: startCell,
        e: endCell,
      };

      ws["!merges"].push(merge);
    });
  }

  return ws;
};

const add_tables_areas_from_list = (wb, table_areas) => {
  if (!wb.Workbook) wb.Workbook = {};
  if (!wb.Workbook.Names) wb.Workbook.Names = [];

  const table_areas_maps = table_areas.map((table_area, index) => {
    return { Name: table_area["name"], Ref: table_area["formulae_range"] };
  });

  wb.Workbook.Names = [...wb.Workbook.Names, ...table_areas_maps];

  wb.Workbook.Names.push({
    Name: "MyData",
    Ref: "'Lookup Tables'!$A$1:$A$2",
  });
  return wb;
};

const add_borders_to_merged_areas = (ws, merged_cells, all_borders) => {
  if (merged_cells) {
    merged_cells.forEach((mergeStr) => {
      const [start, end] = mergeStr.split(":");
      const startCell = XLSX.utils.decode_cell(start);
      const endCell = XLSX.utils.decode_cell(end);

      // Apply borders only to outer edges
      for (let row = startCell.r; row <= endCell.r; row++) {
        for (let col = startCell.c; col <= endCell.c; col++) {
          const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
          ws[cellAddress] = ws[cellAddress] || { v: "", t: "s" };
          ws[cellAddress].s = ws[cellAddress].s || {};

          // Initialize border if not present
          ws[cellAddress].s.border = ws[cellAddress].s.border || {};

          // Apply borders only to outer edges
          if (row === startCell.r || all_borders === true) {
            ws[cellAddress].s.border.top = {
              style: "thin",
              color: { rgb: "000000" },
            };
          }
          if (row === endCell.r || all_borders === true) {
            ws[cellAddress].s.border.bottom = {
              style: "thin",
              color: { rgb: "000000" },
            };
          }
          if (col === startCell.c || all_borders === true) {
            ws[cellAddress].s.border.left = {
              style: "thin",
              color: { rgb: "000000" },
            };
          }
          if (col === endCell.c || all_borders === true) {
            ws[cellAddress].s.border.right = {
              style: "thin",
              color: { rgb: "000000" },
            };
          }
        }
      }
    });
  }

  return ws;
};

/**
 * Function to get RGB or theme color for fgColor and bgColor
 */
function extract_color_from_openpyxl_styles(styleString) {
  const hex_color = extract_style_tag_value(styleString, "rgb");

  if (hex_color) {
    // If the color value is not "None", check if it's a valid RGB color
    return { hex_color: hex_color };
  } else {
    // If it's None, use the theme color
    const theme_color_index = extract_style_tag_value(styleString, `theme`);
    const theme_tint = extract_style_tag_value(styleString, `tint`);

    // Theme and tint go hand in hand, not color, no theme
    if (theme_color_index) {
      return {
        theme_color: excelThemeColors[parseInt(theme_color_index)],
        theme_tint: theme_tint,
      };
    }
  }
  return null;
}

const get_styles_from_obj = (style) => {
  let border = {};
  let font = {};
  let fill = {};
  let alignment = {};

  if (style.fill) {
    fill = {
      fgColor: {
        rgb: style.fill.fgColor.rgb,
      },
    };
  }

  if (style.font) {
    font = {
      color: {
        rgb: style.font.color.rgb,
      },
    };
  }

  if (style.border) {
    border = {
      outline: style.border.outline,
    };
  }

  if (style.alignment) {
    alignment = {
      horizontal: style.alignment,
    };
  }

  return {
    border,
    font,
    fill,
    alignment,
  };
};

function applyTint(hexColor, tintFactor) {
  // Convert hex to RGB
  const r = parseInt(hexColor.substring(0, 2), 16);
  const g = parseInt(hexColor.substring(2, 4), 16);
  const b = parseInt(hexColor.substring(4, 6), 16);

  // Apply tint (tint formula: final = color + (255 - color) * tintFactor)
  const tintedR = Math.round(r + (255 - r) * tintFactor);
  const tintedG = Math.round(g + (255 - g) * tintFactor);
  const tintedB = Math.round(b + (255 - b) * tintFactor);

  // Convert back to hex
  return (
    tintedR.toString(16).padStart(2, "0") +
    tintedG.toString(16).padStart(2, "0") +
    tintedB.toString(16).padStart(2, "0")
  ).toUpperCase();
}

function getColorObj(target_string, target_includes, target_key) {
  const outcome = target_string.includes(target_includes)
    ? extract_style_tag_value(target_string, target_key)
    : null;

  return outcome;
}

function splitBorderString(borderString) {
  // First normalize the string by replacing newlines with tabs
  const normalizedString = borderString.split("Side object");

  // Extract each section for the border sides
  const result: any = {
    leftStyle: getColorObj(normalizedString[1], "style='", "style"),
    rightStyle: getColorObj(normalizedString[2], "style='", "style"),
    topStyle: getColorObj(normalizedString[3], "style='", "style"),
    bottomStyle: getColorObj(normalizedString[4], "style='", "style"),
  };

  const intermColors = {
    leftColor: getColorObj(
      normalizedString[1],
      "<openpyxl.styles.colors.Color object>",
      "rgb"
    ),
    rightColor: getColorObj(
      normalizedString[2],
      "<openpyxl.styles.colors.Color object>",
      "rgb"
    ),
    topColor: getColorObj(
      normalizedString[3],
      "<openpyxl.styles.colors.Color object>",
      "rgb"
    ),
    bottomColor: getColorObj(
      normalizedString[4],
      "<openpyxl.styles.colors.Color object>",
      "rgb"
    ),

    leftTheme: getColorObj(
      normalizedString[1],
      "<openpyxl.styles.colors.Color object>",
      "theme"
    ),
    rightTheme: getColorObj(
      normalizedString[2],
      "<openpyxl.styles.colors.Color object>",
      "theme"
    ),
    topTheme: getColorObj(
      normalizedString[3],
      "<openpyxl.styles.colors.Color object>",
      "theme"
    ),
    bottomTheme: getColorObj(
      normalizedString[4],
      "<openpyxl.styles.colors.Color object>",
      "theme"
    ),

    leftTint: getColorObj(
      normalizedString[1],
      "<openpyxl.styles.colors.Color object>",
      "tint"
    ),
    rightTint: getColorObj(
      normalizedString[2],
      "<openpyxl.styles.colors.Color object>",
      "tint"
    ),
    topTint: getColorObj(
      normalizedString[3],
      "<openpyxl.styles.colors.Color object>",
      "tint"
    ),
    bottomTint: getColorObj(
      normalizedString[4],
      "<openpyxl.styles.colors.Color object>",
      "tint"
    ),

    leftType: getColorObj(
      normalizedString[1],
      "<openpyxl.styles.colors.Color object>",
      "type"
    ),
    rightType: getColorObj(
      normalizedString[2],
      "<openpyxl.styles.colors.Color object>",
      "type"
    ),
    topType: getColorObj(
      normalizedString[3],
      "<openpyxl.styles.colors.Color object>",
      "type"
    ),
    bottomType: getColorObj(
      normalizedString[4],
      "<openpyxl.styles.colors.Color object>",
      "type"
    ),
  };

  if (intermColors.leftType === "indexed" || intermColors.leftType === "auto") {
    result.leftColor = default_border_color;
  } else if (
    intermColors.leftColor != null &&
    intermColors.leftColor != undefined
  ) {
    result.leftColor = intermColors.leftColor;
  } else if (
    intermColors.leftTheme != null &&
    intermColors.leftTheme != undefined
  ) {
    result.leftColor = applyTint(
      excelThemeColors[intermColors.leftTheme],
      intermColors.leftTint || 0
    );
  }

  if (
    intermColors.rightType === "indexed" ||
    intermColors.rightType === "auto"
  ) {
    result.rightColor = default_border_color;
  } else if (
    intermColors.rightColor != null &&
    intermColors.rightColor != undefined
  ) {
    result.rightColor = intermColors.rightColor;
  } else if (
    intermColors.rightTheme != null &&
    intermColors.rightTheme != undefined
  ) {
    result.rightColor = applyTint(
      excelThemeColors[intermColors.rightTheme],
      intermColors.rightTint || 0
    );
  }

  if (intermColors.topType === "indexed" || intermColors.topType === "auto") {
    result.topColor = default_border_color;
  } else if (
    intermColors.topColor != null &&
    intermColors.topColor != undefined
  ) {
    result.topColor = intermColors.topColor;
  } else if (
    intermColors.topTheme != null &&
    intermColors.topTheme != undefined
  ) {
    result.topColor = applyTint(
      excelThemeColors[intermColors.topTheme],
      intermColors.topTint || 0
    );
  }

  if (
    intermColors.bottomType === "indexed" ||
    intermColors.bottomType === "auto"
  ) {
    result.bottomColor = default_border_color;
  } else if (
    intermColors.bottomColor != null &&
    intermColors.bottomColor != undefined
  ) {
    result.bottomColor = intermColors.bottomColor;
  } else if (
    intermColors.bottomTheme != null &&
    intermColors.bottomTheme != undefined
  ) {
    result.bottomColor = applyTint(
      excelThemeColors[intermColors.bottomTheme],
      intermColors.bottomTint || 0
    );
  }

  return result;
}

function get_styles_from_str(cell) {
  let border;
  let font;
  let fill;
  let alignment;

  if (Object.keys(cell).includes("style")) {
    ////////////////////////////////////////
    // Find the cell font
    ////////////////////////////////////////
    font = {
      name: extract_style_tag_value(cell["style"]["font"], "name"),
      b: extract_style_tag_value(cell["style"]["font"], "b") === "True",
      i: extract_style_tag_value(cell["style"]["font"], "i") === "True",
      sz: parseFloat(extract_style_tag_value(cell["style"]["font"], "sz")),
      u: parseFloat(extract_style_tag_value(cell["style"]["font"], "u")),
      color:
        extract_style_tag_value(cell["style"]["font"], "rgb") != "None" &&
        extract_style_tag_value(cell["style"]["font"], "rgb"),
    };

    ////////////////////////////////////////
    // Find the cell back/foreground colors
    ////////////////////////////////////////
    const pattern = extract_style_tag_value(
      cell["style"]["fill"],
      "patternType"
    );

    fill = { patternType: pattern };

    // Input string
    const colors_string = cell["style"]["fill"].split(
      "<openpyxl.styles.colors.Color object>"
    );

    // Extract fgColor and bgColor and get RGB values
    const fgColor = extract_color_from_openpyxl_styles(colors_string[1]);
    const bgColor = extract_color_from_openpyxl_styles(colors_string[2]);

    if (fgColor) {
      if (fgColor.hex_color && fgColor.hex_color !== "00000000") {
        fill.fgColor = fgColor.hex_color;
      } else if (fgColor.theme_color && fgColor.theme_color !== "00000000") {
        // Apply tint if it exists
        fill.fgColor = applyTint(fgColor.theme_color, fgColor.theme_tint || 0);
      }
    }

    if (bgColor) {
      if (bgColor.hex_color && bgColor.hex_color !== "00000000") {
        fill.bgColor = bgColor.hex_color;
      } else if (bgColor.theme_color && bgColor.theme_color !== "00000000") {
        // Apply tint if it exists
        fill.bgColor = applyTint(bgColor.theme_color, bgColor.theme_tint || 0);
      }
    }

    /////////////////////////////////////
    // Find the cell internal alignments
    /////////////////////////////////////
    const alignmentTemp = {
      horizontal: extract_style_tag_value(
        cell["style"]["alignment"],
        "horizontal"
      ),
      vertical: extract_style_tag_value(cell["style"]["alignment"], "vertical"),
      wrapText: extract_style_tag_value(cell["style"]["alignment"], "wrapText"),
      textRotation: parseInt(
        extract_style_tag_value(cell["style"]["alignment"], "textRotation")
      ),
    };

    alignment = {
      ...(alignmentTemp.horizontal && {
        horizontal: alignmentTemp.horizontal,
      }),
      ...(alignmentTemp.vertical && { vertical: alignmentTemp.vertical }),
      ...(alignmentTemp.textRotation && {
        textRotation: alignmentTemp.textRotation,
      }),
      ...(alignmentTemp.wrapText && {
        wrapText: alignmentTemp.wrapText === "True" ? true : false,
      }),
    };

    /////////////////////////////////////
    // Find the border styles
    /////////////////////////////////////
    let border_sty_col_attributes;

    border_sty_col_attributes = splitBorderString(cell["style"]["border"]);

    const left = {
      ...{
        ...(border_sty_col_attributes.leftStyle &&
          border_sty_col_attributes.leftColor && {
            style: "thin",
          }),

        ...(border_sty_col_attributes.leftColor && {
          color: border_sty_col_attributes.leftColor,
        }),
      },
    };

    const top = {
      ...{
        ...(border_sty_col_attributes.topStyle &&
          border_sty_col_attributes.topColor && {
            style: "thin",
          }),

        ...(border_sty_col_attributes.topColor && {
          color: border_sty_col_attributes.topColor,
        }),
      },
    };

    const right = {
      ...{
        ...(border_sty_col_attributes.rightStyle &&
          border_sty_col_attributes.rightColor && {
            style: "thin",
          }),

        ...(border_sty_col_attributes.rightColor && {
          color: border_sty_col_attributes.rightColor,
        }),
      },
    };

    const bottom = {
      ...{
        ...(border_sty_col_attributes.bottomStyle &&
          border_sty_col_attributes.bottomColor && {
            style: "thin",
          }),

        ...(border_sty_col_attributes.bottomColor && {
          color: border_sty_col_attributes.bottomColor,
        }),
      },
    };

    if (cell.merged === true) {
      border = {};
    } else {
      border = {};
      let has_more_than_one_side = false;
      if (
        Object.keys(left).length > 0 ||
        Object.keys(right).length > 0 ||
        Object.keys(bottom).length > 0 ||
        Object.keys(top).length > 0
      ) {
        has_more_than_one_side = true;
      }

      const default_side = { style: "thin", color: "36454F" };
      if (Object.keys(left).length > 0) {
        border.left = left;
      } else {
        if (has_more_than_one_side) {
          border.left = default_side;
        }
      }
      if (Object.keys(right).length > 0) {
        border.right = right;
      } else {
        if (has_more_than_one_side) {
          border.right = default_side;
        }
      }
      if (Object.keys(top).length > 0) {
        border.top = top;
      } else {
        if (has_more_than_one_side) {
          border.top = default_side;
        }
      }
      if (Object.keys(bottom).length > 0) {
        border.bottom = bottom;
      } else {
        if (has_more_than_one_side) {
          border.bottom = default_side;
        }
      }
    }
  }

  return {
    border: border,
    font: font,
    fill: fill,
    alignment: alignment,
  };
}

const process_and_apply_excel_styles = (styledCell) => {
  let styles: any = {};

  if (styledCell.style.font) {
    const font: any = {};

    if (styledCell.style.font.name) font.name = styledCell.style.font.name;
    if (styledCell.style.font.bold) font.bold = styledCell.style.font.bold;
    if (styledCell.style.font.sz) font.sz = styledCell.style.font.sz;
    if (styledCell.style.font.u) font.underline = styledCell.style.font.u;
    if (styledCell.style.font.color && styledCell.style.font.color.rgb) {
      font.color = { rgb: styledCell.style.font.color.rgb };
    }

    // Only add font to styles if it has at least one property
    if (Object.keys(font).length > 0) {
      styles.font = font;
    }
  }

  if (styledCell.style.fill) {
    const fill: any = {};

    if (styledCell.style.fill.patternType)
      fill.patternType = styledCell.style.fill.patternType;
    if (styledCell.style.fill.fgColor && styledCell.style.fill.fgColor.rgb) {
      fill.fgColor = { rgb: styledCell.style.fill.fgColor.rgb };
    }
    if (styledCell.style.fill.bgColor && styledCell.style.fill.bgColor.rgb) {
      fill.bgColor = { rgb: styledCell.style.fill.bgColor.rgb };
    }

    // Only add fill to styles if it has at least one property
    if (Object.keys(fill).length > 0) {
      styles.fill = fill;
    }
  }

  if (
    styledCell.style.border &&
    Object.keys(styledCell.style.border).length > 0
  ) {
    styles.border = styledCell.style.border;
  }

  if (
    styledCell.style.alignment &&
    Object.keys(styledCell.style.alignment).length > 0
  ) {
    styles.alignment = styledCell.style.alignment;
  }

  return styles;
};

const process_and_apply_excel_styles_to_str = (styles) => {
  const border = styles["border"];
  const font = styles["font"];
  const fill = styles["fill"];
  const alignment = styles["alignment"];

  const styles_object = {
    // Add
    font: {
      ...(font && font.name && { name: font.name }),
      ...(font && font.b && { bold: font.b }),
      ...(font && font.sz && { sz: font.sz }),
      ...(font && font.u && { underline: font.u }),
      ...(font && font.color && { color: { rgb: font.color } }),
    },

    // Add background fill
    ...(fill &&
      fill.patternType &&
      (fill.fgColor || fill.bgColor) && {
        fill: {
          ...(fill.fgColor && { fgColor: { rgb: fill.fgColor } }),
          ...(fill.bgColor && { bgColor: { rgb: fill.bgColor } }),
          ...(fill.bgColor ||
            (fill.fgColor && { patternType: fill.patternType })),
        },
      }),

    // Add the border keys
    ...(border && Object.keys(border).length > 0 && { border: border }),

    // Add the alignment keys
    ...(alignment &&
      Object.keys(alignment).length > 0 && { alignment: alignment }),
  };

  return styles_object;
};

const process_and_apply_openpyxl_styles = (ws, sheet_data) => {
  if (!ws["!rows"]) ws["!rows"] = [];
  if (!ws["!cols"]) ws["!cols"] = [];

  let column_widths = {};

  // Process all styles
  sheet_data.forEach((row, rowIndex) => {
    row.forEach((cell, colIndex) => {
      // Get the cell's Excel sheet Address
      const cellAddress = XLSX.utils.encode_cell({
        r: rowIndex,
        c: colIndex,
      });

      if (cell["formula"]) {
        /**
         * There is a 'formulae_replacements' object at the top that allows us to customize how to
         * translate openpyxl equations for this SimpleJs
         */
        const modified_cell = Object.keys(formulae_replacements).reduce(
          (modifiedValue, pattern) => {
            return modifiedValue.replace(
              new RegExp(pattern, "g"),
              formulae_replacements[pattern]
            );
          },
          cell["value"]
        );

        ws[cellAddress].f = modified_cell;
      }

      let styles;

      if (!ws[cellAddress]) {
        // Initialize the cell if it doesn't exist (for null value cells)
        ws[cellAddress] = { v: "" };
      }

      // Use the appropriate function based on whether the styles are in string or object format
      if (typeof cell.style.fill === "string") {
        styles = get_styles_from_str(cell);

        // Format the styles in the way the Excel API expects
        ws[cellAddress].s = process_and_apply_excel_styles_to_str(styles);
      } else {
        styles = get_styles_from_obj(cell.style);
        console.log(`get_styles_from_obj: ${JSON.stringify(styles)}`);

        // Format the styles in the way the Excel API expects
        ws[cellAddress].s = process_and_apply_excel_styles({ style: styles });
      }

      // Set the column heights for each col: we want the col to be the width of the widest cell (should be the same but I see differences in testing)
      if (column_widths[colIndex]) {
        column_widths[colIndex] =
          cell.column_dimensions &&
          cell.column_dimensions > column_widths[colIndex]
            ? cell.column_dimensions
            : column_widths[colIndex];
      } else {
        column_widths[colIndex] = cell.column_dimensions;
      }
    });

    // Update the row height
    const max_h = Math.max(
      ...row.map((cell) => cell.row_dimensions && cell.row_dimensions)
    );

    ws["!rows"][rowIndex] = { hpt: max_h != undefined ? max_h : 10 };
  });

  Object.keys(column_widths).forEach((colIndex) => {
    ws["!cols"][colIndex] = { 
      wch: column_widths[colIndex],
      // Explicitly set hidden to false to ensure column visibility
      hidden: false
    };
  });

  return ws;
};

const orchistrate_worksheet_workflow = (data, sheet_name, wb, table_ranges) => {
  // Reorder the potentially unordered list of Excel positions (left to right) e.g. Start at column A
  console.log(data);
  data.merged_cells = reorder_merge_list_alphanumerically(data["merged_cells"]);

  /**
     *  Using the list of Excel Areas for Merged Cells, Passing to fill where the merge will go
        By default Excel exports ignore this and the data ignores it, thus when we go to apply the merges, 
        it overwrites cells that are a or a few columns too early from their intended column from a user perspective
     */
  const adjustedData = pad_merged_cell_rows(
    data["sheet_data"],
    data["merged_cells"]
  );

  /**
   * Create the worksheet
   */
  let ws = XLSX.utils.aoa_to_sheet(adjustedData["values"]);
  
  // Initialize column definitions if they don't exist
  if (!ws["!cols"]) ws["!cols"] = [];

  /**
   * Merge all cells marked for merging from the 'merged_cells' list
   * @Params
   * ws = XLSX worksheet
   * data["merged_cells"] = list of Excel cell positions
   */
  ws = merge_cells_from_merge_list(ws, data["merged_cells"]);

  /**
   * Apply styles to each sell, povided in the cell object by openpyxl
   */
  ws = process_and_apply_openpyxl_styles(ws, adjustedData["data"]);
  
  // Ensure all merged columns have appropriate width and visibility
  // Use the tracked merged column indices from pad_merged_cell_rows
  if (adjustedData.mergedColumnIndices && adjustedData.mergedColumnIndices instanceof Set) {
    Array.from(adjustedData.mergedColumnIndices as Set<number>).forEach((colIndex: number) => {
      if (!ws["!cols"]) {
        ws["!cols"] = [];
      }
      
      if (!ws["!cols"][colIndex]) {
        ws["!cols"][colIndex] = { wch: 8.43, hidden: false };
      } else {
        // Ensure column isn't hidden
        const col = ws["!cols"][colIndex];
        if (col) {
          col.hidden = false;
        
          // Set minimum width if too narrow
          if (!col.wch || col.wch < 8.43) {
            col.wch = 8.43;
          }
        }
      }
    });
  }

  /**
   * Add borders around all merged cells
   */
  ws = add_borders_to_merged_areas(ws, data["merged_cells"], false);

  /**
   * Add the worksheet to the workbook
   */
  update_workbook_with_worksheet(wb, ws, sheet_name);

  /**
   * Create Excel Tables on the ranges we have marked as tables
   */
  if (table_ranges) {
    wb = add_tables_areas_from_list(wb, table_ranges);

    // Apply borders on all sides for every cell in these tables
    ws = add_borders_to_merged_areas(
      ws,
      table_ranges.map((range) => range["range"]),
      true
    );
  }

  return ws;
};

const update_workbook_with_worksheet = (wb, ws, sheet_name) => {
  XLSX.utils.book_append_sheet(wb, ws, sheet_name);
};
